import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as SettingsActions from '../actions/settings.actions';
import * as AuthActions from '../../auth/actions/auth.actions';
import * as Reducers from '../reducers';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { StorageService } from '../../../core/storage/storage.service';
import { environment } from '../../../../environments/environment';
import moment from 'moment-timezone';
import { SettingsService } from '../../../core/services/settings.service';
import { forkJoin, of } from 'rxjs';
import { AccountSettingsService } from '../../../settings/services/account-settings.service';
import { TagEnum } from '../../../settings/models/tag.enum';
import { SettingsModel } from '../../../core/models/settings.model';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { LoadingBarState } from '@ngx-loading-bar/core/loading-bar.state';
import { Store } from '@ngrx/store';

@Injectable()
export class SettingsEffects {

  setTimeZone$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.setTimeZone.type),
      tap((action: any) => {
        this._storageService.set('timeZone', action.timeZone);
        moment.tz.setDefault(action.timeZone);
      })
    ), { dispatch: false }
  );

  loadDefaultTimeZone$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.loadDefaultTimeZone.type),
      tap((action: any) => {
        const timeZone = this._storageService.get('timeZone') || moment.tz.guess();
        moment.tz.setDefault(timeZone);
      })
    ), { dispatch: false }
  );


  loadTimeZone$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.getSettingsTagsSuccess.type),
      tap((action: any) => {
        if (action.data && action.data.length > 0) {
          const timeZoneSettings: SettingsModel = action.data.find(x => x.key.toUpperCase() === TagEnum.TimeZone.name.toUpperCase());
          if (timeZoneSettings) {
            const timeZone = timeZoneSettings.value || this._storageService.get('timeZone') || moment.tz.guess();
            this._storageService.set('timeZone', timeZone);
            moment.tz.setDefault(timeZone);
          }
        }
      })
    ), { dispatch: false }
  );



  getSettings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.getSettingsTags.type),
      switchMap(action => this._settingsService.getSettings()
        .pipe(
          map(res => SettingsActions.getSettingsTagsSuccess({data: res})),
          catchError(error => of(SettingsActions.getSettingsTagsFailure(error)))
        )
      )
    )
  );

  getTag$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.getTag),
      withLatestFrom(this._store.select(Reducers.getSettings)),
      switchMap(([action, settings]) => {
        const cached = settings.find(s => s.key.toUpperCase() === action.tagName.toUpperCase());
        if (cached) {
          return of(SettingsActions.getTagSuccess({data: cached}));
        }
        return this._settingsService.getTag(action.tagName).pipe(
          map(res => SettingsActions.getTagSuccess({data: res})),
          catchError(error => of(SettingsActions.getTagFailure(error)))
        );
      })
    )
  );

  getTags$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.getTags),
      withLatestFrom(this._store.select(Reducers.getSettings)),
      switchMap(([action, settings]) => {
        const allCached = action.tagNames.every(name =>
          settings.find(s => s.key.toUpperCase() === name.toUpperCase())
        );
        if (allCached) {
          const data = action.tagNames.map(name =>
            settings.find(s => s.key.toUpperCase() === name.toUpperCase())
          );
          return of(SettingsActions.getTagsSuccess({data}));
        }
        const subs = action.tagNames.map(tagName => this._settingsService.getTag(tagName));
        return forkJoin(subs).pipe(
          map((res: SettingsModel[]) => SettingsActions.getTagsSuccess({data: res})),
          catchError(error => of(SettingsActions.getTagFailure(error)))
        );
      })
    )
  );

  editTag$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.editTag),
      tap(() => this._loader.start()),
      withLatestFrom(this._store.select(Reducers.getSettings)),
      // mergeMap, not switchMap: saving several fields at once dispatches several editTag
      // actions in the same tick, and switching would cancel all but the last request.
      mergeMap(([action, settings]) => {
        const previousData = settings.find(s => s.key.toUpperCase() === action.tagName.toUpperCase());
        return this._settingsService.editTag(action.tagName, action.tagValue).pipe(
          map(res => SettingsActions.editTagSuccess({data: res})),
          catchError(error => of(SettingsActions.editTagFailure({error, previousData}))),
          tap(() => this._loader.complete()),
        );
      })
    )
  );

  editTags$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.editTags),
      tap(() => this._loader.start()),
      withLatestFrom(this._store.select(Reducers.getSettings)),
      switchMap(([action, settings]) => {
        const previousData = settings.filter(s =>
          action.tagNames.some(name => name.toUpperCase() === s.key.toUpperCase())
        );
        const subs = action.tagNames.map(tagName => this._settingsService.editTag(tagName, action.tagValue));
        return forkJoin(subs).pipe(
          map((res: SettingsModel[]) => SettingsActions.editTagsSuccess({data: res})),
          catchError(error => of(SettingsActions.editTagsFailure({error, previousData}))),
          tap(() => this._loader.complete()),
        );
      })
    )
  );


  deleteTag$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.deleteTag.type),
      mergeMap(action => this._settingsService.deleteTag(action['tagName'])
        .pipe(
          map(() => SettingsActions.deleteTagSuccess({tagName: action['tagName']})),
          catchError(error => of(SettingsActions.deleteTagFailure(error)))
        )
      )
    )
  );

  persistSettings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.getSettingsTagsSuccess),
      tap((action: any) => {
        try {
          localStorage.setItem(`${environment.storagePrefix}.cachedSettings`, JSON.stringify(action.data));
        } catch { }
      })
    ), { dispatch: false }
  );

  deleteAccount$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.deleteAccount.type),
      switchMap(action => this._accountSettingsService.deleteAccount()
        .pipe(
          map(() => AuthActions.logout()),
        )
      )
    )
  );

  changeUserAccount$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.changeUserAccount.type),
      tap(() => this._loader.start()),
      switchMap(action => this._accountSettingsService.changeUserAccount(action['data'])
        .pipe(
          map(() => SettingsActions.changeUserAccountSuccess({data: action['data']})),
          catchError(error => of(SettingsActions.changeUserAccountFailure(error))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  private _loader: LoadingBarState;

  constructor(
    private _actions$: Actions,
    private _store: Store<any>,
    private _storageService: StorageService,
    private _settingsService: SettingsService,
    private _accountSettingsService: AccountSettingsService,
    private _loadingBar: LoadingBarService
  ) {
    this._loader = this._loadingBar.useRef('main');
  }
}
